// src/pages/admin/CoopMembership.jsx
// Admin interface for cooperative membership management

import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@/lib/firebase";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, onSnapshot, orderBy, limit, startAfter, deleteDoc } from "firebase/firestore";
import { ToastContext } from "@/components/ToastProvider";
import useUserProfile from '../../hooks/useUserProfile';
import useCoopMembership from '../../hooks/useCoopMembership';
import { useAuth } from '../../AuthContext';
import NewMembersPayment from '../../components/NewMembersPayment';
import { TableSkeleton } from '../../components/LoadingStates';
import useDebounce from '../../hooks/useDebounce';
import { 
  COOP_STATUS, 
  COOP_CLASS, 
  getMembershipStatusText, 
  getMembershipClassText,
  getMembershipStatusBadgeClass 
} from '../../lib/coopMembers';

// Available roles for cooperative members
const AVAILABLE_ROLES = [
  'cooperative Member', // Default role for approved members
  'admin',
  'treasurer', 
  'manager',
  'general manager',
  'cashier',
  'delivery',
  'rider',
  'staff',
  'BOD' // Board of Directors
];

// Button component for consistent styling
const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  onClick, 
  className = '',
  ...props 
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500',
    outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-indigo-500'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  
  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <button className={classes} disabled={disabled} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

// Badge component for consistent status display
const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800 border-gray-200',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    danger: 'bg-red-100 text-red-800 border-red-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200'
  };
  
  const classes = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[variant]} ${className}`;
  
  return <span className={classes}>{children}</span>;
};

export default function CoopMembership() {
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve', 'reject', 'suspend'
  const [actionNotes, setActionNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [paymentFilter, setPaymentFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Debounce search term to reduce API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Search loading state
  const [isSearching, setIsSearching] = useState(false);
  
  // Show search loading indicator when search term changes
  useEffect(() => {
    if (searchTerm && searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  }, [searchTerm, debouncedSearchTerm]);
  
  // Verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationMember, setVerificationMember] = useState(null);

  // Payment verification modal state
  const [showPaymentVerificationModal, setShowPaymentVerificationModal] = useState(false);
  const [paymentId, setPaymentId] = useState(''); // Store the actual payment ID from payments collection
  const [verifyingPaymentType, setVerifyingPaymentType] = useState(''); // Track which payment type is being verified
  
  // Bulk operations state
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Member status update state
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [statusUpdateForm, setStatusUpdateForm] = useState({
    status: '',
    notes: '',
    class: '',
    phone: '',
    address: '',
    occupation: '',
    businessType: '',
    annualIncome: ''
  });

  // Role management state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingMemberRoles, setEditingMemberRoles] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [allMembersWithRoles, setAllMembersWithRoles] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  // Local busy/error state for updates
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Listen for payment status changes to update verification flags
  useEffect(() => {
    if (!verificationMember || !verificationMember.uid) return;

    let entranceFeeUnsubscribe = null;
    let shareCapitalUnsubscribe = null;

    const setupListeners = async () => {
      try {
        const { db } = await import('@/lib/firebase');
        const { collection, query, where, onSnapshot } = await import('firebase/firestore');

        // Listen for entrance fee payment changes
        if (verificationMember.entranceFeePaymentId) {
          const entranceFeeQuery = query(
            collection(db, 'payments'),
            where('__name__', '==', verificationMember.entranceFeePaymentId)
          );
          
          entranceFeeUnsubscribe = onSnapshot(entranceFeeQuery, async (snapshot) => {
            if (!snapshot.empty) {
              const paymentData = snapshot.docs[0].data();
              const isPosted = paymentData.status === 'paid' && (paymentData.receiptNo || paymentData.journalNo);
              
              if (isPosted) {
                // Update local state
                setVerificationMember(prev => ({
                  ...prev,
                  entranceFeePosted: true,
                  entranceFeeVerified: true
                }));

                // Update the member document in the database
                try {
                  const { doc, updateDoc } = await import('firebase/firestore');
                  await updateDoc(doc(db, 'members', verificationMember.uid), {
                    entranceFeePosted: true,
                    entranceFeeVerified: true
                  });
                } catch (error) {
                  // Error updating entrance fee verification status
                }
              }
            }
          });
        }

        // Listen for share capital payment changes
        if (verificationMember.shareCapitalPaymentId) {
          const shareCapitalQuery = query(
            collection(db, 'payments'),
            where('__name__', '==', verificationMember.shareCapitalPaymentId)
          );
          
          shareCapitalUnsubscribe = onSnapshot(shareCapitalQuery, async (snapshot) => {
            if (!snapshot.empty) {
              const paymentData = snapshot.docs[0].data();
              const isPosted = paymentData.status === 'paid' && (paymentData.receiptNo || paymentData.journalNo);
              
              if (isPosted) {
                // Update local state
                setVerificationMember(prev => ({
                  ...prev,
                  shareCapitalPosted: true,
                  shareCapitalVerified: true
                }));

                // Update the member document in the database
                try {
                  const { doc, updateDoc } = await import('firebase/firestore');
                  await updateDoc(doc(db, 'members', verificationMember.uid), {
                    shareCapitalPosted: true,
                    shareCapitalVerified: true
                  });
                        } catch (error) {
          // Error updating share capital verification status
        }
              }
            }
          });
        }
              } catch (error) {
          // Error setting up payment listeners
        }
    };

    setupListeners();

    // Cleanup function
    return () => {
      if (entranceFeeUnsubscribe) entranceFeeUnsubscribe();
      if (shareCapitalUnsubscribe) shareCapitalUnsubscribe();
    };
  }, [verificationMember?.uid, verificationMember?.entranceFeePaymentId, verificationMember?.shareCapitalPaymentId]);

  const { profile } = useUserProfile();
  const { notify } = useContext(ToastContext);
  const { user } = useAuth();
  const {
    getPendingApplications,
    getApprovedMembers,
    searchMembers,
    updateMembershipStatus,
    assignMemberId,
    loading,
    error
  } = useCoopMembership();

  const [pendingMembers, setPendingMembers] = useState([]);
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      try {
        if (activeTab === 'pending') {
          const result = await getPendingApplications();
          if (result.success) {
            setPendingMembers(result.data);
          } else {
            // Failed to get pending applications
          }
        } else if (activeTab === 'approved') {
          const result = await getApprovedMembers();
          if (result.success) {
            setApprovedMembers(result.data);
          } else {
            // Failed to get approved members
          }
        } else if (activeTab === 'search') {
          const criteria = {};
          if (filterClass) criteria.class = filterClass;
          
          const result = await searchMembers(criteria);
          if (result.success) {
            setFilteredMembers(result.data);
          } else {
            // Failed to search members
          }
        } else if (activeTab === 'roles') {
          await loadAllMembersWithRoles();
        }
      } catch (error) {
        // Error in loadData
      }
    };

    loadData();
  }, [activeTab, filterClass]);

  // Filter members by search term and advanced filters
  const getDisplayMembers = () => {
    let members = [];
    if (activeTab === 'pending') members = pendingMembers;
    else if (activeTab === 'approved') members = approvedMembers;
    else if (activeTab === 'search') members = filteredMembers;

    // Apply search term filter
    if (debouncedSearchTerm) {
      members = members.filter(member => {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const fullName = `${member.firstName || ''} ${member.middleName || ''} ${member.lastName || ''}`.toLowerCase();
        
        return (
          member.uid?.toLowerCase().includes(searchLower) ||
          member.memberId?.toLowerCase().includes(searchLower) ||
          member.applicationNotes?.toLowerCase().includes(searchLower) ||
          fullName.includes(searchLower) ||
          member.phone?.toLowerCase().includes(searchLower) ||
          member.address?.toLowerCase().includes(searchLower) ||
          member.occupation?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply class filter
    if (filterClass) {
      members = members.filter(member => member.class === filterClass);
    }

    // Apply status filter
    if (filterStatus) {
      members = members.filter(member => member.status === filterStatus);
    }

    // Apply date filter
    if (dateFilter.from || dateFilter.to) {
      members = members.filter(member => {
        if (!member.createdAt) return false;
        
        const memberDate = new Date(member.createdAt.seconds * 1000);
        const fromDate = dateFilter.from ? new Date(dateFilter.from) : null;
        const toDate = dateFilter.to ? new Date(dateFilter.to + 'T23:59:59') : null;
        
        if (fromDate && memberDate < fromDate) return false;
        if (toDate && memberDate > toDate) return false;
        return true;
      });
    }

    // Apply payment filter
    if (paymentFilter) {
      members = members.filter(member => {
        switch (paymentFilter) {
          case 'entrance_paid':
            return member.dues?.entranceFeePaid || member.entranceFeePayment;
          case 'entrance_unpaid':
            return !member.dues?.entranceFeePaid && !member.entranceFeePayment;
          case 'shares_paid':
            return member.shareCapitalPayment;
          case 'shares_unpaid':
            return !member.shareCapitalPayment;
          case 'all_paid':
            return (member.dues?.entranceFeePaid || member.entranceFeePayment) && member.shareCapitalPayment;
          case 'partial_paid':
            return ((member.dues?.entranceFeePaid || member.entranceFeePayment) && !member.shareCapitalPayment) || 
                   (!(member.dues?.entranceFeePaid || member.entranceFeePayment) && member.shareCapitalPayment);
          default:
            return true;
        }
      });
    }

    return members;
  };

  // Handle member action (approve/reject/suspend)
  const handleMemberAction = async () => {
    if (!selectedMember || !actionType) return;

  // Open action modal for individual member actions
  const openActionModal = (member, action) => {
    setSelectedMember(member);
    setActionType(action);
    setActionNotes('');
    setShowModal(true);
  };

    const result = await updateMembershipStatus(
      selectedMember.uid,
      actionType === 'approve' ? COOP_STATUS.APPROVED : 
      actionType === 'reject' ? COOP_STATUS.REJECTED : 
      COOP_STATUS.SUSPENDED,
      profile?.id,
      actionNotes
    );

    if (result.success) {
      setShowModal(false);
      setSelectedMember(null);
      setActionType('');
      setActionNotes('');
      
      // Refresh the current tab data
      const event = new Event('refresh-data');
      window.dispatchEvent(event);
      
      // Reload data for current tab
      if (activeTab === 'pending') {
        const refreshResult = await getPendingApplications();
        if (refreshResult.success) {
          setPendingMembers(refreshResult.data);
        }
      }
    }
  };

  // Open action modal
  const openActionModal = (member, action) => {
    setSelectedMember(member);
    setActionType(action);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedMember(null);
    setActionType('');
    setActionNotes('');
  };

  // Open verification modal
  const openVerificationModal = (member) => {
          // Opening verification modal for member
    setVerificationMember(member);
    setShowVerificationModal(true);
          // Modal state set
  };

  // Close verification modal
  const closeVerificationModal = () => {
    setShowVerificationModal(false);
    setVerificationMember(null);
  };

  // Handle verification action (approve/reject)
  const handleVerificationAction = async (action) => {
    if (!verificationMember) return;

    const result = await updateMembershipStatus(
      verificationMember.uid,
      action === 'approve' ? COOP_STATUS.APPROVED : COOP_STATUS.REJECTED,
      user?.uid || '',
      action === 'reject' ? actionNotes : ''
    );

    if (result.success) {
      // Show success message with member ID if approved
      if (action === 'approve' && result.memberId) {
        notify(`Application approved successfully! Member ID: ${result.memberId}`, { type: 'success', duration: 5000 });
      } else {
        notify(`Application ${action === 'approve' ? 'approved' : 'rejected'} successfully!`, { type: 'success' });
      }
      closeVerificationModal();
      setActionNotes('');
      
      // Refresh the current tab data
      if (activeTab === 'pending') {
        const refreshResult = await getPendingApplications();
        if (refreshResult.success) {
          setPendingMembers(refreshResult.data);
        }
      }
    } else {
      notify(`Failed to ${action} application: ${result.error}`, { type: 'error' });
    }
  };

  // Open payment verification modal
  const openPaymentVerification = async (type) => {
    if (!verificationMember) return;
    
    try {
      // Find the payment in the payments collection
      const { db } = await import('@/lib/firebase');
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      // Query for the specific payment type for this member (any status)
      const paymentType = type === 'entrance_fee' ? 'membership_fee' : 'share_capital';
      
      const paymentsQuery = query(
        collection(db, 'payments'),
        where('userId', '==', verificationMember.uid),
        where('type', '==', paymentType)
        // Removed status filter to find any payment of this type
      );
      
      const querySnapshot = await getDocs(paymentsQuery);
      
      if (!querySnapshot.empty) {
        const paymentDoc = querySnapshot.docs[0]; // Get the first matching payment
        const paymentData = paymentDoc.data();
        
        // CoopMembership - Payment found
        
        setPaymentId(paymentDoc.id); // Set the payment ID
        setVerifyingPaymentType(type); // Set the type being verified
        
        // Update verification member with payment ID for listening
        if (type === 'entrance_fee') {
          setVerificationMember(prev => ({
            ...prev,
            entranceFeePaymentId: paymentDoc.id,
            entranceFeePayment: true // Also set the boolean flag
          }));
        } else if (type === 'share_capital') {
          setVerificationMember(prev => ({
            ...prev,
            shareCapitalPaymentId: paymentDoc.id,
            shareCapitalPayment: true // Also set the boolean flag
          }));
        }
        
        setShowPaymentVerificationModal(true);
      } else {
        // No payment found for this member
        // Use alert instead of notify since notify might not be defined
        alert(`No ${type.replace('_', ' ')} payment found for this member.`);
      }
    } catch (error) {
      // Error opening payment verification modal
      alert('Failed to open payment verification. Please try again.');
    }
  };

  // Close payment verification modal
  const closePaymentVerificationModal = () => {
    setShowPaymentVerificationModal(false);
    setPaymentId('');
    setVerifyingPaymentType(''); // Reset the type
  };

  // Bulk operations functions
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const selectAllMembers = () => {
    const currentMembers = getDisplayMembers();
    setSelectedMembers(new Set(currentMembers.map(m => m.uid)));
  };

  const clearSelection = () => {
    setSelectedMembers(new Set());
  };

  const openBulkModal = (action) => {
    if (selectedMembers.size === 0) {
      notify('Please select at least one member first', { type: 'warning' });
      return;
    }
    setBulkAction(action);
    setShowBulkModal(true);
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkAction('');
    setBulkNotes('');
    setBulkProcessing(false);
  };

  const executeBulkAction = async () => {
    if (selectedMembers.size === 0) return;
    
    setBulkProcessing(true);
    const results = { success: 0, failed: 0, errors: [] };
    
    try {
      // Update member documents
      for (const memberId of selectedMembers) {
        const memberRef = doc(db, 'members', memberId);
        await updateDoc(memberRef, {
          status: bulkAction,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || '',
          adminNotes: bulkNotes || ''
        });
      }
      
      // Show results
      if (results.success > 0) {
        notify(`Bulk operation completed: ${results.success} successful, ${results.failed} failed`, { 
          type: results.failed === 0 ? 'success' : 'warning',
          duration: 5000
        });
      }
      
      if (results.failed > 0) {
        // Bulk operation errors
      }
      
      // Refresh data and close modal
      closeBulkModal();
      clearSelection();
      
      // Refresh current tab data
      if (activeTab === 'pending') {
        const refreshResult = await getPendingApplications();
        if (refreshResult.success) {
          setPendingMembers(refreshResult.data);
        }
      } else if (activeTab === 'approved') {
        const refreshResult = await getApprovedMembers();
        if (refreshResult.success) {
          setApprovedMembers(refreshResult.data);
        }
      }
      
    } catch (error) {
      notify(`Bulk operation failed: ${error.message}`, { type: 'error' });
    } finally {
      setBulkProcessing(false);
    }
  };

  const exportMemberData = () => {
    const currentMembers = getDisplayMembers();
    const selectedData = currentMembers.filter(m => selectedMembers.has(m.uid));
    
    if (selectedData.length === 0) {
      notify('Please select at least one member to export', { type: 'warning' });
      return;
    }
    
    // Prepare CSV data
    const csvData = selectedData.map(member => ({
      'Member ID': member.memberId || 'N/A',
      'UID': member.uid,
      'First Name': member.firstName || '',
      'Last Name': member.lastName || '',
      'Middle Name': member.middleName || '',
      'Phone': member.phone || '',
      'Address': member.address || '',
      'Class': getMembershipClassText(member.class) || '',
      'Status': getMembershipStatusText(member.status) || '',
      'Entrance Fee Paid': member.dues?.entranceFeePaid ? 'Yes' : 'No',
      'Share Capital': member.dues?.shares || 0,
      'Application Date': member.createdAt ? new Date(member.createdAt.seconds * 1000).toLocaleDateString() : 'N/A',
      'Notes': member.applicationNotes || ''
    }));
    
    // Convert to CSV
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header]}"`).join(','))
    ].join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `coop-members-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notify(`Exported ${selectedData.length} member records`, { type: 'success' });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterClass('');
    setFilterStatus('');
    setDateFilter({ from: '', to: '' });
    setPaymentFilter('');
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchTerm || filterClass || filterStatus || dateFilter.from || dateFilter.to || paymentFilter;
  };

  // Automatically update member verification status when payments are posted
  const updateMemberVerificationStatus = async (memberUid, paymentType, isPosted) => {
    try {
      const { getFirestore, doc, updateDoc } = await import('firebase/firestore');
      const db = getFirestore();

      const updates = {};
      
      if (paymentType === 'entrance_fee') {
        updates.entranceFeePosted = isPosted;
        updates.entranceFeeVerified = isPosted;
      } else if (paymentType === 'share_capital') {
        updates.shareCapitalPosted = isPosted;
        updates.shareCapitalVerified = isPosted;
      }

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, 'members', memberUid), updates);
      }
    } catch (error) {
              // Error updating member verification status
    }
  };

  // Member Status Update Functions
  const openStatusUpdateModal = (member) => {
    setEditingMember(member);
    setStatusUpdateForm({
      status: member.status || '',
      notes: member.applicationNotes || '',
      class: member.class || '',
      phone: member.phone || '',
      address: member.address || '',
      occupation: member.occupation || '',
      businessType: member.businessType || '',
      annualIncome: member.annualIncome || ''
    });
    setShowStatusUpdateModal(true);
  };

  const closeStatusUpdateModal = () => {
    setShowStatusUpdateModal(false);
    setEditingMember(null);
    setStatusUpdateForm({
      status: '',
      notes: '',
      class: '',
      phone: '',
      address: '',
      occupation: '',
      businessType: '',
      annualIncome: ''
    });
  };

  const updateMemberStatus = async () => {
    if (!editingMember || !user?.uid) return;

    try {
      setBusy(true);
      setErr("");

      const { getFirestore, doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const db = getFirestore();

      // Resolve member document ID robustly
      const resolvedUid = (typeof editingMember?.uid === 'string' && editingMember.uid.trim())
        || (typeof editingMember?.id === 'string' && editingMember.id.trim())
        || (typeof editingMember?.memberId === 'string' && editingMember.memberId.trim())
        || '';

      if (!resolvedUid) {
        const msg = 'Cannot update: missing member UID.';
        setErr(msg);
        notify(msg, { type: 'error' });
        console.error('CoopMembership.updateMemberStatus: missing member UID on editingMember =', editingMember);
        return;
      }

      // Update the member document
      const memberRef = doc(db, 'members', resolvedUid);
      await updateDoc(memberRef, {
        ...statusUpdateForm,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });

      notify('Member status updated successfully', { type: 'success' });
      closeStatusUpdateModal();
      
      // Refresh current tab data using existing loaders
      if (activeTab === 'pending') {
        const refreshResult = await getPendingApplications();
        if (refreshResult.success) {
          setPendingMembers(refreshResult.data);
        }
      } else if (activeTab === 'approved') {
        const refreshResult = await getApprovedMembers();
        if (refreshResult.success) {
          setApprovedMembers(refreshResult.data);
        }
      }
    } catch (error) {
      // Error updating member status
      const msg = error?.message || 'Failed to update member status';
      setErr(msg);
      notify(msg, { type: 'error' });
      console.error('CoopMembership.updateMemberStatus error:', error);
    } finally {
      setBusy(false);
    }
  };

  // Handle payment verification action (approve/reject)
  const handlePaymentVerificationAction = async (action) => {
    if (!verificationMember) return;

    const result = await updateMembershipStatus(
      verificationMember.uid,
      action === 'approve' ? COOP_STATUS.APPROVED : COOP_STATUS.REJECTED,
      user?.uid || '',
      action === 'reject' ? actionNotes : ''
    );

    if (result.success) {
      closePaymentVerificationModal();
      setActionNotes('');
      
      // Refresh the current tab data
      if (activeTab === 'pending') {
        const refreshResult = await getPendingApplications();
        if (refreshResult.success) {
          setPendingMembers(refreshResult.data);
        }
      }
    }
  };

  // Handle payment verification from NewMembersPayment component
  const handlePaymentVerified = async (paymentData) => {
    try {
      // Close the payment verification modal
      closePaymentVerificationModal();
      
      // Update the verification member's payment status based on the verified payment
      if (verificationMember && paymentData) {
        const paymentType = paymentData.type;
        
        if (paymentType === 'membership_fee') {
          setVerificationMember(prev => ({
            ...prev,
            entranceFeeVerified: true,
            entranceFeePosted: paymentData.status === 'paid'
          }));
        } else if (paymentType === 'share_capital') {
          setVerificationMember(prev => ({
            ...prev,
            shareCapitalVerified: true,
            shareCapitalPosted: paymentData.status === 'paid'
          }));
        }
      }
      
      // Refresh the current tab data to show updated payment status
      if (activeTab === 'pending') {
        const refreshResult = await getPendingApplications();
        if (refreshResult.success) {
          setPendingMembers(refreshResult.data);
        }
      }
      
      // Show success notification
      notify('Payment verification completed successfully', { type: 'success' });
      
    } catch (error) {
              // Error handling payment verification
      notify('Error processing payment verification', { type: 'error' });
    }
  };

  // Check if all required payments are verified
  const arePaymentsVerified = () => {
    if (!verificationMember) return false;
    
    // Check entrance fee payment - must have payment AND be verified
    const entranceFeeVerified = verificationMember.entranceFeePayment && 
      verificationMember.entranceFeeVerified === true;
    
    // Check share capital payment - must have payment AND be verified
    const shareCapitalVerified = verificationMember.shareCapitalPayment && 
      verificationMember.shareCapitalVerified === true;
    
    return entranceFeeVerified && shareCapitalVerified;
  };

  // Check if all required payments are posted
  const arePaymentsPosted = () => {
    if (!verificationMember) return false;
    
    // Check entrance fee payment - must be posted
    const entranceFeePosted = verificationMember.entranceFeePosted === true;
    
    // Check share capital payment - must be posted
    const shareCapitalPosted = verificationMember.shareCapitalPosted === true;
    
    return entranceFeePosted && shareCapitalPosted;
  };

  // Role management functions
  const loadAllMembersWithRoles = async () => {
    setRoleLoading(true);
    try {
      const { getFirestore, collection, query, getDocs, orderBy, where } = await import('firebase/firestore');
      const db = getFirestore();
      
      // Only get APPROVED members from the members collection
      const membersQuery = query(
        collection(db, 'members'),
        where('status', '==', COOP_STATUS.APPROVED),
        orderBy('createdAt', 'desc')
      );
      
      const membersSnapshot = await getDocs(membersQuery);
      const membersData = membersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          ...data,
          // Use the role field from members collection, default to 'cooperative Member' for approved members
          role: data.role || 'cooperative Member',
          displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown'
        };
      });

      setAllMembersWithRoles(membersData);
    } catch (error) {
              // Error loading members with roles
      notify('Error loading members with roles', { type: 'error' });
    } finally {
      setRoleLoading(false);
    }
  };

  const openRoleModal = (member) => {
    // Only allow role management for approved members
    if (member.status !== COOP_STATUS.APPROVED) {
      notify('Only approved cooperative members can have roles assigned', { type: 'warning' });
      return;
    }
    
    setEditingMemberRoles(member);
    setSelectedRoles(new Set([member.role || 'cooperative Member']));
    setShowRoleModal(true);
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setEditingMemberRoles(null);
    setSelectedRoles(new Set(['cooperative Member']));
  };

  const toggleRole = (role) => {
    // Only allow one role per member
    setSelectedRoles(new Set([role]));
  };

  const saveMemberRoles = async () => {
    if (!editingMemberRoles || !user?.uid) return;

    try {
      const { getFirestore, doc, updateDoc, getDoc, serverTimestamp } = await import('firebase/firestore');
      const db = getFirestore();

              // Attempting to update role for member
              // Selected role

      // First, verify the member document exists
      const memberDocRef = doc(db, 'members', editingMemberRoles.uid);
      const memberDoc = await getDoc(memberDocRef);
      
      if (!memberDoc.exists()) {
        throw new Error(`Member document does not exist: ${editingMemberRoles.uid}`);
      }
      
              // Member document exists, proceeding with update

      // Get the selected role (only one role allowed per member)
      const selectedRole = selectedRoles.size > 0 ? Array.from(selectedRoles)[0] : 'cooperative Member';
      
      // Validate role assignment based on membership status
      if (selectedRole !== 'cooperative Member' && selectedRole !== 'delivery' && selectedRole !== 'rider') {
        // Check if member is approved (cooperative member)
        if (editingMemberRoles.status !== COOP_STATUS.APPROVED) {
          throw new Error('Only approved cooperative members can be assigned administrative roles');
        }
      }
      
      // Ensure that only approved members can be assigned the 'cooperative Member' role
      if (editingMemberRoles.status !== COOP_STATUS.APPROVED) {
        throw new Error('Only approved members can be assigned roles');
      }

      // Update the member document with the new role
      await updateDoc(memberDocRef, {
        role: selectedRole,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });

      notify('Member role updated successfully', { type: 'success' });
      closeRoleModal();
      
      // Refresh the roles data
      loadAllMembersWithRoles();
    } catch (error) {
              // Error updating member role
      notify(`Error updating member role: ${error.message}`, { type: 'error' });
    }
  };

  // Filter members by role search and filter
  const getFilteredMembersWithRoles = () => {
    let filtered = allMembersWithRoles;

    // Apply role search filter
    if (roleSearchTerm) {
      const searchLower = roleSearchTerm.toLowerCase();
      filtered = filtered.filter(member => {
        const fullName = `${member.firstName || ''} ${member.middleName || ''} ${member.lastName || ''}`.toLowerCase();
        const displayName = member.displayName?.toLowerCase() || '';
        const memberId = member.memberId?.toLowerCase() || '';
        const uid = member.uid?.toLowerCase() || '';
        
        return (
          fullName.includes(searchLower) ||
          displayName.includes(searchLower) ||
          memberId.includes(searchLower) ||
          uid.includes(searchLower)
        );
      });
    }

    // Apply role filter
    if (roleFilter) {
      if (roleFilter === 'website_user') {
        filtered = filtered.filter(member => member.status !== COOP_STATUS.APPROVED);
      } else {
        filtered = filtered.filter(member => 
          member.status === COOP_STATUS.APPROVED && member.role === roleFilter
        );
      }
    }

    return filtered;
  };

  // Memoize the filtered members to prevent unnecessary recalculations
  const displayMembers = useMemo(() => getDisplayMembers(), [
    debouncedSearchTerm, 
    filterClass, 
    filterStatus, 
    dateFilter, 
    paymentFilter, 
    pendingMembers, 
    approvedMembers, 
    filteredMembers, 
    activeTab
  ]);

  return (
    <div className="space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Cooperative Membership</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage member applications and membership status</p>
        </div>
      </div>

      {/* Tabs - Mobile Optimized */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-2 sm:gap-8 overflow-x-auto pb-2">
          {[
            { id: 'pending', label: 'Pending Applications', count: pendingMembers.length },
            { id: 'approved', label: 'Approved Members', count: approvedMembers.length },
            { id: 'search', label: 'Search & Filter', count: filteredMembers.length },
            { id: 'roles', label: 'Roles Management', count: allMembersWithRoles.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2 flex-shrink-0`}
            >
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              {tab.count > 0 && (
                <span className={`${
                  activeTab === tab.id ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-900'
                } inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

              {/* Enhanced Search and Filter Bar - Mobile Optimized */}
      <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Main Search Bar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <div className="flex-1 min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors duration-200 pr-10 text-sm sm:text-base"
                title="Search by name, member ID, phone, address, occupation"
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  {isSearching ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  ) : (
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-end gap-2 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="w-full sm:w-auto min-w-0 sm:min-w-[140px] text-sm"
            >
              <span className="hidden sm:inline">{showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}</span>
              <span className="sm:hidden">{showAdvancedFilters ? 'Hide' : 'Filters'}</span>
            </Button>
            {hasActiveFilters() && (
              <Button
                variant="danger"
                size="sm"
                onClick={clearAllFilters}
                className="w-full sm:w-auto min-w-0 sm:min-w-[100px] text-sm"
              >
                <span className="hidden sm:inline">Clear All</span>
                <span className="sm:hidden">Clear</span>
              </Button>
            )}
          </div>
        </div>

        {/* Advanced Filters - Mobile Optimized */}
        {showAdvancedFilters && (
          <div className="border-t pt-3 sm:pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Membership Class Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Membership Class</label>
                <select
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                >
                  <option value="">All Classes</option>
                  <option value={COOP_CLASS.FARMER}>Farmer</option>
                  <option value={COOP_CLASS.CONSUMER}>Consumer</option>
                  <option value={COOP_CLASS.ASSOCIATE}>Associate</option>
                  <option value={COOP_CLASS.INSTITUTION}>Institution</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value={COOP_STATUS.PENDING}>Pending</option>
                  <option value={COOP_STATUS.APPROVED}>Approved</option>
                  <option value={COOP_STATUS.REJECTED}>Rejected</option>
                  <option value={COOP_STATUS.SUSPENDED}>Suspended</option>
                </select>
              </div>

              {/* Payment Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                >
                  <option value="">All Payments</option>
                  <option value="entrance_paid">Entrance Fee Paid</option>
                  <option value="entrance_unpaid">Entrance Fee Unpaid</option>
                  <option value="shares_paid">Share Capital Paid</option>
                  <option value="shares_unpaid">Share Capital Unpaid</option>
                  <option value="all_paid">All Payments Complete</option>
                  <option value="partial_paid">Partial Payments</option>
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Date</label>
                <div className="space-y-2">
                  <input
                    type="date"
                    value={dateFilter.from}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={dateFilter.to}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Summary */}
        {hasActiveFilters() && (
          <div className="border-t pt-3">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700">Active filters:</span>
              {searchTerm && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 max-w-full">
                  <span className="truncate">Search: "{searchTerm}"</span>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:text-blue-600 flex-shrink-0"
                  >
                    ×
                  </button>
                </span>
              )}
              {filterClass && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 max-w-full">
                  <span className="truncate">Class: {getMembershipClassText(filterClass)}</span>
                  <button
                    onClick={() => setFilterClass('')}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:text-green-600 flex-shrink-0"
                  >
                    ×
                  </button>
                </span>
              )}
              {filterStatus && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 max-w-full">
                  <span className="truncate">Status: {getMembershipStatusText(filterStatus)}</span>
                  <button
                    onClick={() => setFilterStatus('')}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-purple-400 hover:text-purple-600 flex-shrink-0"
                  >
                    ×
                  </button>
                </span>
              )}
              {paymentFilter && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 max-w-full">
                  <span className="truncate">Payment: {paymentFilter.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  <button
                    onClick={() => setPaymentFilter('')}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-yellow-400 hover:text-yellow-600 flex-shrink-0"
                  >
                    ×
                  </button>
                </span>
              )}
              {(dateFilter.from || dateFilter.to) && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 max-w-full">
                  <span className="truncate">Date: {dateFilter.from || '...'} - {dateFilter.to || '...'}</span>
                  <button
                    onClick={() => setDateFilter({ from: '', to: '' })}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:text-indigo-600 flex-shrink-0"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          <TableSkeleton rows={5} columns={4} />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <details className="mt-2">
                <summary className="text-sm font-medium text-red-700 cursor-pointer">Error Details</summary>
                <pre className="mt-1 text-xs text-red-600 bg-red-100 p-2 rounded overflow-auto">
                  {JSON.stringify(error, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Operations Toolbar - Mobile Optimized */}
      {!loading && !error && selectedMembers.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-blue-900">
                {selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear selection
              </button>
            </div>
            <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
              <button
                onClick={() => openBulkModal('approve')}
                disabled={activeTab !== 'pending'}
                className="px-2 sm:px-3 py-1.5 bg-green-600 text-white text-xs sm:text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title={activeTab !== 'pending' ? 'Can only approve pending applications' : 'Approve selected applications'}
              >
                <span className="hidden sm:inline">Approve Selected</span>
                <span className="sm:hidden">Approve</span>
              </button>
              <button
                onClick={() => openBulkModal('reject')}
                disabled={activeTab !== 'pending'}
                className="px-2 sm:px-3 py-1.5 bg-red-600 text-white text-xs sm:text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title={activeTab !== 'pending' ? 'Can only reject pending applications' : 'Reject selected applications'}
              >
                <span className="hidden sm:inline">Reject Selected</span>
                <span className="sm:hidden">Reject</span>
              </button>
              <button
                onClick={() => openBulkModal('suspend')}
                disabled={activeTab !== 'approved'}
                className="px-2 sm:px-3 py-1.5 bg-yellow-600 text-white text-xs sm:text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title={activeTab !== 'approved' ? 'Can only suspend approved members' : 'Suspend selected members'}
              >
                <span className="hidden sm:inline">Suspend Selected</span>
                <span className="sm:hidden">Suspend</span>
              </button>
              <button
                onClick={exportMemberData}
                className="px-2 sm:px-3 py-1.5 bg-blue-600 text-white text-xs sm:text-sm rounded-md hover:bg-blue-700 col-span-2 sm:col-span-1"
              >
                <span className="hidden sm:inline">Export Selected</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members Table */}
      {!loading && !error && activeTab !== 'roles' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {/* Table Header with Selection Controls - Mobile Optimized */}
          <div className="bg-gray-50 px-3 sm:px-6 py-3 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedMembers.size === getDisplayMembers().length && getDisplayMembers().length > 0}
                    onChange={selectAllMembers}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Select All</span>
                </label>
                {selectedMembers.size > 0 && (
                  <span className="text-sm text-gray-500">
                    {selectedMembers.size} of {getDisplayMembers().length} selected
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-500">
                {getDisplayMembers().length} member{getDisplayMembers().length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <ul className="divide-y divide-gray-200">
            {displayMembers.length === 0 ? (
              <li className="px-6 py-8 text-center">
                <p className="text-gray-500">
                  {activeTab === 'pending' && 'No pending applications'}
                  {activeTab === 'approved' && 'No approved members'}
                  {activeTab === 'search' && 'No members found'}
                </p>
              </li>
            ) : (
              displayMembers.map((member) => (
                <li key={member.uid} className="px-3 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedMembers.has(member.uid)}
                        onChange={() => toggleMemberSelection(member.uid)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {member.firstName && member.lastName ? `${member.firstName} ${member.middleName ? member.middleName + ' ' : ''}${member.lastName}` : (member.memberId || member.uid)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {getMembershipClassText(member.class)} • {getMembershipStatusText(member.status)}
                              {member.memberId && (
                                <span className="ml-2 font-mono text-indigo-600">#{member.memberId}</span>
                              )}
                            </p>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getMembershipStatusBadgeClass(member.status)} self-start`}>
                            {getMembershipStatusText(member.status)}
                          </span>
                        </div>
                        
                        {member.applicationNotes && (
                          <p className="mt-2 text-sm text-gray-600 truncate">
                            {member.applicationNotes}
                          </p>
                        )}
                        
                        <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-gray-500">
                          <span>
                            Entrance Fee: {member.dues?.entranceFeePaid || member.entranceFeePayment ? 'Paid' : 'Not Paid'}
                            {member.entranceFeeVerified && <span className="ml-1 text-green-600">✓ Verified</span>}
                            {member.entranceFeePosted && <span className="ml-1 text-blue-600">✓ Posted</span>}
                          </span>
                          <span>
                            Shares: ₱{member.dues?.shares || member.shareCapitalAmount || 0}
                            {member.shareCapitalVerified && <span className="ml-1 text-green-600">✓ Verified</span>}
                            {member.shareCapitalPosted && <span className="ml-1 text-blue-600">✓ Posted</span>}
                          </span>
                          {member.createdAt && (
                            <span>Applied: {new Date(member.createdAt.seconds * 1000).toLocaleDateString()}</span>
                          )}
                        </div>
                        
                        {/* Role Display */}
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Role: </span>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                            {member.status === COOP_STATUS.APPROVED ? 
                              (member.role || 'Cooperative Member').charAt(0).toUpperCase() + (member.role || 'Cooperative Member').slice(1) :
                              'Website User'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      {activeTab === 'pending' && (
                        <button
                          onClick={() => openVerificationModal(member)}
                          className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Verify
                        </button>
                      )}
                      
                      <button
                        onClick={() => openStatusUpdateModal(member)}
                        className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-blue-300 text-xs font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Edit
                      </button>
                      
                      {activeTab === 'approved' && (
                        <button
                          onClick={() => openActionModal(member, 'suspend')}
                          className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Suspend
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Roles Management Tab Content */}
      {activeTab === 'roles' && (
        <div className="space-y-6">
          {/* Role Summary - Mobile Optimized */}
          <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3 sm:mb-4">Role Distribution</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
              {/* Website Users (Unapproved) */}
              <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-gray-600">
                  {allMembersWithRoles.filter(member => member.status !== COOP_STATUS.APPROVED).length}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Website User</div>
              </div>
              
              {/* Cooperative Members (Approved) */}
              <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-indigo-600">
                  {allMembersWithRoles.filter(member => 
                    member.status === COOP_STATUS.APPROVED && (member.role || 'cooperative Member') === 'cooperative Member'
                  ).length}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Cooperative Member</div>
              </div>
              
              {/* Other Roles (Only for approved members) */}
              {AVAILABLE_ROLES.filter(role => role !== 'cooperative Member').map(role => {
                const count = allMembersWithRoles.filter(member => 
                  member.status === COOP_STATUS.APPROVED && member.role === role
                ).length;
                return (
                  <div key={role} className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="text-xl sm:text-2xl font-bold text-indigo-600">{count}</div>
                    <div className="text-xs sm:text-sm text-gray-600 capitalize">{role}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Roles Search and Filter */}
          <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Members</label>
                <input
                  type="text"
                  placeholder="Search by name, member ID, or UID..."
                  value={roleSearchTerm}
                  onChange={(e) => setRoleSearchTerm(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end gap-2 flex-shrink-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Role</label>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-offset-2 focus:ring-indigo-500 text-sm"
                  >
                    <option value="">All Roles</option>
                    <option value="website_user">Website User</option>
                    {AVAILABLE_ROLES.map(role => (
                      <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="outline"
                  onClick={loadAllMembersWithRoles}
                  disabled={roleLoading}
                  className="w-full sm:w-auto min-w-0 sm:min-w-[100px] text-sm"
                >
                  {roleLoading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </div>
          </div>

          {/* Roles Table - Mobile Optimized */}
          {roleLoading ? (
            <div className="space-y-6">
              <TableSkeleton rows={5} columns={4} />
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="bg-gray-50 px-3 sm:px-6 py-3 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                  <h3 className="text-lg font-medium text-gray-900">Member Roles</h3>
                  <div className="text-sm text-gray-500">
                    {getFilteredMembersWithRoles().length} member{getFilteredMembersWithRoles().length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <ul className="divide-y divide-gray-200">
                {getFilteredMembersWithRoles().length === 0 ? (
                  <li className="px-3 sm:px-6 py-8 text-center">
                    <p className="text-gray-500">No members found</p>
                  </li>
                ) : (
                  getFilteredMembersWithRoles().map((member) => (
                    <li key={member.uid} className="px-3 sm:px-6 py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {member.displayName || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown'}
                              </p>
                              <p className="text-sm text-gray-500">
                                {member.memberId && (
                                  <span className="font-mono text-indigo-600">#{member.memberId}</span>
                                )}
                                {member.uid && (
                                  <span className="ml-2 text-gray-500">UID: {member.uid}</span>
                                )}
                              </p>
                            </div>
                          </div>
                          
                          {/* Current Role Display */}
                          <div className="mt-2">
                            <p className="text-xs font-medium text-gray-700 mb-1">Current Role:</p>
                            <div className="flex flex-wrap gap-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                {member.status === COOP_STATUS.APPROVED ? 
                                  (member.role || 'Cooperative Member').charAt(0).toUpperCase() + (member.role || 'Cooperative Member').slice(1) :
                                  'Website User'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                          <button
                            onClick={() => openRoleModal(member)}
                            disabled={member.status !== COOP_STATUS.APPROVED}
                            className={`inline-flex items-center px-2 sm:px-3 py-1.5 border text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              member.status === COOP_STATUS.APPROVED
                                ? 'border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 focus:ring-indigo-500'
                                : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                            }`}
                            title={member.status !== COOP_STATUS.APPROVED ? 'Only approved members can have roles assigned' : 'Manage member roles'}
                          >
                            <span className="hidden sm:inline">Manage Roles</span>
                            <span className="sm:hidden">Manage</span>
                          </button>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Action Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {actionType === 'approve' && 'Approve Membership Application'}
                      {actionType === 'reject' && 'Reject Membership Application'}
                      {actionType === 'suspend' && 'Suspend Membership'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {actionType === 'approve' && 'This will approve the membership application and grant the user full cooperative member benefits.'}
                        {actionType === 'reject' && 'This will reject the membership application. Please provide a reason for rejection.'}
                        {actionType === 'suspend' && 'This will suspend the membership and revoke member benefits. Please provide a reason for suspension.'}
                      </p>
                      
                      {selectedMember && (
                        <div className="mt-3 p-3 bg-gray-50 rounded">
                          <p className="text-sm"><strong>Member:</strong> {selectedMember.memberId || selectedMember.uid}</p>
                          <p className="text-sm"><strong>Class:</strong> {getMembershipClassText(selectedMember.class)}</p>
                          <p className="text-sm"><strong>Status:</strong> {getMembershipStatusText(selectedMember.status)}</p>
                        </div>
                      )}
                      
                      {(actionType === 'reject' || actionType === 'suspend') && (
                        <div className="mt-4">
                          <label htmlFor="action-notes" className="block text-sm font-medium text-gray-700">
                            {actionType === 'reject' ? 'Rejection Reason' : 'Suspension Reason'}
                          </label>
                          <textarea
                            id="action-notes"
                            rows={3}
                            value={actionNotes}
                            onChange={(e) => setActionNotes(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder={actionType === 'reject' ? 'Please explain why this application is being rejected...' : 'Please explain why this membership is being suspended...'}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleMemberAction}
                  disabled={loading || (actionType !== 'approve' && !actionNotes.trim())}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    actionType === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  } ${loading || (actionType !== 'approve' && !actionNotes.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Processing...' : (
                    actionType === 'approve' ? 'Approve' : 
                    actionType === 'reject' ? 'Reject' : 'Suspend'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && verificationMember && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto" style={{ position: 'fixed', zIndex: 9999 }}>
          <div className="flex items-center justify-center min-h-screen p-4 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9998]" style={{ position: 'fixed', zIndex: 9998 }} onClick={closeVerificationModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-4xl sm:w-full max-h-[90vh] z-[9999] relative" style={{ position: 'relative', zIndex: 9999 }}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 overflow-y-auto custom-scrollbar max-h-[calc(90vh-200px)]">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Verify Membership Application
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Review the complete application details before making a decision.
                    </p>
                  </div>
                  {/* Close Button */}
                  <div className="ml-auto flex-shrink-0">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={closeVerificationModal}
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Application Details */}
                <div className="mt-6 space-y-6">
                  {/* Personal Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Personal Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Full Name</p>
                        <p className="text-sm text-gray-900">
                          {verificationMember.firstName} {verificationMember.middleName ? verificationMember.middleName + ' ' : ''}{verificationMember.lastName}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Birth Information</p>
                        <p className="text-sm text-gray-900">
                          {verificationMember.birthdate} • {verificationMember.birthplace}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Demographics</p>
                        <p className="text-sm text-gray-900">
                          {verificationMember.sex} • {verificationMember.civilStatus}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Special Categories</p>
                        <p className="text-sm text-gray-900">
                          {verificationMember.pwd ? 'PWD' : ''} {verificationMember.senior ? 'Senior Citizen' : ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Contact & Membership */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Contact & Membership</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Phone</p>
                        <p className="text-sm text-gray-900">{verificationMember.phone}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Address</p>
                        <p className="text-sm text-gray-900">{verificationMember.address}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Membership Class</p>
                        <p className="text-sm text-gray-900">{getMembershipClassText(verificationMember.class)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Application Date</p>
                        <p className="text-sm text-gray-900">
                          {verificationMember.createdAt ? new Date(verificationMember.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Financial Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Entrance Fee</p>
                        <div className="flex items-center gap-2">
                          <p className={`text-sm ${verificationMember.entranceFeePayment ? 'text-green-600' : 'text-red-600'}`}>
                            {verificationMember.entranceFeePayment ? '✓ Paid' : '✗ Not Paid'}
                          </p>
                          {verificationMember.entranceFeePayment && (
                            <button
                              onClick={() => openPaymentVerification('entrance_fee')}
                              className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                            >
                              Verify Payment
                            </button>
                          )}
                        </div>
                        {verificationMember.entranceFeePayment && (
                          <div className="mt-1 text-xs text-gray-600">
                            Amount: ₱{verificationMember.entranceFeeAmount || 300}
                            {verificationMember.entranceFeeVerified && (
                              <span className="ml-2 text-green-600">✓ Verified</span>
                            )}
                            {verificationMember.entranceFeePosted && (
                              <span className="ml-2 text-blue-600">✓ Posted</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Share Capital</p>
                        <div className="flex items-center gap-2">
                          <p className={`text-sm ${verificationMember.shareCapitalPayment ? 'text-green-600' : 'text-red-600'}`}>
                            {verificationMember.shareCapitalPayment ? '✓ Paid' : '✗ Not Paid'}
                          </p>
                          {verificationMember.shareCapitalPayment && (
                            <button
                              onClick={() => openPaymentVerification('share_capital')}
                              className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                            >
                              Verify Payment
                            </button>
                          )}
                        </div>
                        {verificationMember.shareCapitalPayment && (
                          <div className="mt-1 text-xs text-gray-600">
                            Amount: ₱{verificationMember.shareCapitalAmount || 0}
                            {verificationMember.shareCapitalVerified && (
                              <span className="ml-2 text-blue-600">✓ Verified</span>
                            )}
                            {verificationMember.shareCapitalPosted && (
                              <span className="ml-2 text-blue-600">✓ Posted</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Occupation</p>
                        <p className="text-sm text-gray-900">{verificationMember.occupation || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Annual Income</p>
                        <p className="text-sm text-gray-900">₱{verificationMember.annualIncome || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* KYC Documents */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">KYC Documents</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {verificationMember.kycDocs?.idFront && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Valid ID (Front)</p>
                          <img 
                            src={verificationMember.kycDocs.idFront} 
                            alt="ID Front" 
                            className="w-full h-32 object-cover rounded border"
                          />
                        </div>
                      )}
                      {verificationMember.kycDocs?.idBack && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Valid ID (Back)</p>
                          <img 
                            src={verificationMember.kycDocs.idBack} 
                            alt="ID Back" 
                            className="w-full h-32 object-cover rounded border"
                          />
                        </div>
                      )}
                      {verificationMember.kycDocs?.selfie && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Recent Photo</p>
                          <img 
                            src={verificationMember.kycDocs.selfie} 
                            alt="Selfie" 
                            className="w-full h-32 object-cover rounded border"
                          />
                        </div>
                      )}
                      {verificationMember.kycDocs?.proofOfAddress && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Proof of Address</p>
                          <img 
                            src={verificationMember.kycDocs.proofOfAddress} 
                            alt="Proof of Address" 
                            className="w-full h-32 object-cover rounded border"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Decision Notes */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Decision Notes</h4>
                    <div>
                      <label htmlFor="verification-notes" className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (required for rejection)
                      </label>
                      <textarea
                        id="verification-notes"
                        rows={3}
                        value={actionNotes}
                        onChange={(e) => setActionNotes(e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="Add any notes about your decision..."
                      />
                    </div>
                  </div>

                  {/* Payment Verification Status */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Payment Verification Status</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">Entrance Fee:</span>
                        {verificationMember.entranceFeePayment ? (
                          <div className="flex items-center gap-1">
                            {verificationMember.entranceFeeVerified ? (
                              <span className="text-green-600">✓ Verified</span>
                            ) : (
                              <span className="text-yellow-600">⚠ Pending Verification</span>
                            )}
                            {verificationMember.entranceFeePosted && (
                              <span className="text-blue-600">✓ Posted</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-red-600">✗ Not Paid</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">Share Capital:</span>
                        {verificationMember.shareCapitalPayment ? (
                          <div className="flex items-center gap-1">
                            {verificationMember.shareCapitalVerified ? (
                              <span className="text-green-600">✓ Verified</span>
                            ) : (
                              <span className="text-yellow-600">⚠ Pending Verification</span>
                            )}
                            {verificationMember.shareCapitalPosted && (
                              <span className="text-blue-600">✓ Posted</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-red-600">✗ Not Paid</span>
                        )}
                      </div>
                    </div>
                    
                    {(!arePaymentsVerified() || !arePaymentsPosted()) && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md mt-3">
                        <p className="text-sm text-yellow-800">
                          ⚠️ All payments must be verified and posted before accepting the application.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row gap-3 justify-end">
                      <button
                        type="button"
                        onClick={() => handleVerificationAction('approve')}
                        disabled={loading || !arePaymentsVerified() || !arePaymentsPosted()}
                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Accept Application'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleVerificationAction('reject')}
                        disabled={loading || !actionNotes.trim()}
                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Reject Application'}
                      </button>
                      <button
                        type="button"
                        onClick={closeVerificationModal}
                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Verification Modal */}
      {showPaymentVerificationModal && paymentId && (
        <NewMembersPayment 
          id={paymentId}
          adminUid={user?.uid || ''}
          onPaymentVerified={handlePaymentVerified}
          onClose={() => setShowPaymentVerificationModal(false)}
        />
      )}

      {/* Member Status Update Modal */}
      {showStatusUpdateModal && editingMember && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeStatusUpdateModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Edit Member Information
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Update {editingMember.firstName} {editingMember.lastName}'s information and status
                    </p>
                    {err && (
                      <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                        {err}
                      </div>
                    )}
                    
                    <div className="mt-6 space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Status */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                          </label>
                          <select
                            value={statusUpdateForm.status}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          >
                            <option value={COOP_STATUS.PENDING}>Pending</option>
                            <option value={COOP_STATUS.APPROVED}>Approved</option>
                            <option value={COOP_STATUS.REJECTED}>Rejected</option>
                            <option value={COOP_STATUS.SUSPENDED}>Suspended</option>
                          </select>
                        </div>

                        {/* Membership Class */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Membership Class
                          </label>
                          <select
                            value={statusUpdateForm.class}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, class: e.target.value }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          >
                            <option value="">Select Class</option>
                            <option value={COOP_CLASS.FARMER}>Farmer</option>
                            <option value={COOP_CLASS.CONSUMER}>Consumer</option>
                            <option value={COOP_CLASS.ASSOCIATE}>Associate</option>
                            <option value={COOP_CLASS.INSTITUTION}>Institution</option>
                          </select>
                        </div>

                        {/* Phone */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={statusUpdateForm.phone}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, phone: e.target.value }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="09xxxxxxxxx"
                          />
                        </div>

                        {/* Occupation */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Occupation
                          </label>
                          <input
                            type="text"
                            value={statusUpdateForm.occupation}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, occupation: e.target.value }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="Enter occupation"
                          />
                        </div>

                        {/* Business Type */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Business Type
                          </label>
                          <input
                            type="text"
                            value={statusUpdateForm.businessType}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, businessType: e.target.value }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="e.g., Agriculture, Retail, etc."
                          />
                        </div>

                        {/* Annual Income */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Annual Income (₱)
                          </label>
                          <input
                            type="number"
                            value={statusUpdateForm.annualIncome}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, annualIncome: e.target.value }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="0"
                            min="0"
                          />
                        </div>
                      </div>

                      {/* Address */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Address
                        </label>
                        <textarea
                          value={statusUpdateForm.address}
                          onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, address: e.target.value }))}
                          rows={2}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          placeholder="Complete address"
                        />
                      </div>

                                              {/* Notes */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Admin Notes
                          </label>
                          <textarea
                            value={statusUpdateForm.notes}
                            onChange={(e) => setStatusUpdateForm(prev => ({ ...prev, notes: e.target.value }))}
                            rows={3}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="Add notes about this member or status change..."
                          />
                        </div>

                        {/* Quick Role Management */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quick Role Management
                          </label>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openRoleModal(editingMember)}
                              disabled={editingMember?.status !== COOP_STATUS.APPROVED}
                              className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                editingMember?.status === COOP_STATUS.APPROVED
                                  ? 'border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 focus:ring-indigo-500'
                                  : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                              }`}
                              title={editingMember?.status !== COOP_STATUS.APPROVED ? 'Only approved members can have roles assigned' : 'Click to assign or remove roles for this member'}
                            >
                              Manage Roles
                            </button>
                            <span className="text-xs text-gray-500">
                              {editingMember?.status === COOP_STATUS.APPROVED 
                                ? 'Click to assign or remove roles for this member'
                                : 'Only approved members can have roles assigned'
                              }
                            </span>
                          </div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={updateMemberStatus}
                  disabled={busy}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    busy
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  }`}
                >
                  {busy ? 'Updating…' : 'Update Member'}
                </button>
                <button
                  type="button"
                  onClick={closeStatusUpdateModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Operations Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeBulkModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {bulkAction === 'approve' && 'Approve Selected Applications'}
                      {bulkAction === 'reject' && 'Reject Selected Applications'}
                      {bulkAction === 'suspend' && 'Suspend Selected Members'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {bulkAction === 'approve' && `This will approve ${selectedMembers.size} membership application${selectedMembers.size !== 1 ? 's' : ''} and grant full cooperative member benefits.`}
                        {bulkAction === 'reject' && `This will reject ${selectedMembers.size} membership application${selectedMembers.size !== 1 ? 's' : ''}. Please provide a reason for rejection.`}
                        {bulkAction === 'suspend' && `This will suspend ${selectedMembers.size} membership${selectedMembers.size !== 1 ? 's' : ''} and revoke member benefits. Please provide a reason for suspension.`}
                      </p>
                      
                      <div className="mt-3 p-3 bg-gray-50 rounded">
                        <p className="text-sm"><strong>Selected:</strong> {selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''}</p>
                      </div>
                      
                      {(bulkAction === 'reject' || bulkAction === 'suspend') && (
                        <div className="mt-4">
                          <label htmlFor="bulk-notes" className="block text-sm font-medium text-gray-700">
                            {bulkAction === 'reject' ? 'Rejection Reason' : 'Suspension Reason'}
                          </label>
                          <textarea
                            id="bulk-notes"
                            rows={3}
                            value={bulkNotes}
                            onChange={(e) => setBulkNotes(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-500 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder={bulkAction === 'reject' ? 'Please explain why these applications are being rejected...' : 'Please explain why these memberships are being suspended...'}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={executeBulkAction}
                  disabled={bulkProcessing || (bulkAction !== 'approve' && !bulkNotes.trim())}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    bulkAction === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : bulkAction === 'reject'
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                  } ${bulkProcessing || (bulkAction !== 'approve' && !bulkNotes.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {bulkProcessing ? 'Processing...' : (
                    bulkAction === 'approve' ? 'Approve Selected' : 
                    bulkAction === 'reject' ? 'Reject Selected' : 'Suspend Selected'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeBulkModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      {showRoleModal && editingMemberRoles && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeRoleModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Manage Member Roles
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Assign or remove roles for {editingMemberRoles.displayName || `${editingMemberRoles.firstName || ''} ${editingMemberRoles.lastName || ''}`.trim() || 'this member'}
                    </p>
                    
                    <div className="mt-6">
                      {/* Member Info */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Member Information</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Name:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {editingMemberRoles.displayName || `${editingMemberRoles.firstName || ''} ${editingMemberRoles.lastName || ''}`.trim() || 'Unknown'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Member ID:</span>
                            <span className="ml-2 font-mono text-indigo-600">
                              {editingMemberRoles.memberId || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Status:</span>
                            <span className="ml-2">
                              {getMembershipStatusText(editingMemberRoles.status)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Class:</span>
                            <span className="ml-2">
                              {getMembershipClassText(editingMemberRoles.class)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Role Selection */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Select Role</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Choose the role for this member. Each member can have only one role.
                        </p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {AVAILABLE_ROLES.map(role => (
                            <label key={role} className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer ${
                              selectedRoles.has(role) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                            }`}>
                              <input
                                type="radio"
                                name="memberRole"
                                checked={selectedRoles.has(role)}
                                onChange={() => toggleRole(role)}
                                className="border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-900 capitalize">
                                  {role}
                                </span>
                                <p className="text-xs text-gray-500">
                                  {role === 'cooperative Member' && 'Default role for all cooperative members'}
                                  {role === 'admin' && 'Full system access and control'}
                                  {role === 'treasurer' && 'Financial management and reporting'}
                                  {role === 'manager' && 'Operational management and oversight'}
                                  {role === 'general manager' && 'Overall business management'}
                                  {role === 'cashier' && 'Payment processing and cash handling'}
                                  {role === 'delivery' && 'Delivery operations and logistics'}
                                  {role === 'rider' && 'Delivery personnel'}
                                  {role === 'cooperative_store_manager' && 'Manage cooperative store operations and products'}
                                  {role === 'member_store_manager' && 'Manage member store operations and products'}
                                  {role === 'establishment_store_manager' && 'Manage establishment store operations and products'}
                                  {role === 'staff' && 'General staff access'}
                                  {role === 'BOD' && 'Board of Directors access'}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={saveMemberRoles}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={closeRoleModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
